<template>
  <div class="container-fluid">
    <ValidationObserver v-slot="{ handleSubmit }" ref="observer">
      <div class="row vh-100">
        <div
          class="steps-wrapper col-lg-4 d-lg-block d-md-none d-none d-sm-none p-5"
        >
          <div class="pb-5 logo-wrapper mx-auto">
            <img
              class="img-fluid"
              src="../../assets/img/Acounto_logo.svg"
              alt="Acounto logo"
            />
          </div>
          <div class="p-0 mt-5">
            <StepIndicator
              v-for="item in stepStates"
              :key="item.id"
              :currentStep="currentStep"
              :id="item.id"
              :title="item.title"
              :description="item.description"
            >
            </StepIndicator>
          </div>
        </div>

        <div class="col-12 col-lg-8 p-md-5 py-2">
          <div class="help-wrapper px-0 text-muted text-right small font-poppins mt-4">
            <span class="mr-2">{{ $cte("regExt.askForHelp") }}</span>
            <a href="mailto:hello@acounto.com">{{
              $cte("regExt.askForHelpLink")
            }}</a>
          </div>

          <div class="d-lg-none py-3 logo-wrapper">
            <img
              class=""
              src="../../assets/img/Acounto_logo.svg"
              alt="Acounto logo"
            />
          </div>

          <div class="form-wrapper pt-0 font-jumper">
            <form @submit.prevent="handleSubmit(onSubmit)" novalidate>
              <!-- Step 1-->
              <fieldset v-if="currentStep === 1">
                <div class="step-1">
                  <div class="py-3 py-lg-5 loginDataTitle">
                    <h1 class="h3 font-jumperbold" v-html="$cte('regExt.loginDataTitle')"></h1>
                    <p class="text-muted font-poppins">
                      {{ $cte("regExt.hasAccount") }}
                      <a :href="loginLink" class="cursor-pointer">{{
                        $cte("regExt.hasAccountLink")
                      }}</a>
                    </p>
                  </div>

                  <validation-provider
                    :rules="{ required: true, email: true, isMailUnique: true }"
                    v-slot="validationContext"
                    mode="lazy"
                    ref="email"
                  >
                    <b-form-group :label="$cte('regExt.formLabelEmail')">
                      <b-form-input
                        v-model.trim="loginData.email"
                        autocomplete="off"
                        type="email"
                        id="email"
                        :state="getValidationState(validationContext)"
                        aria-describedby="email"
                        :placeholder="$cte('regExt.formPlaceholdEmail')"
                        required
                      />
                      <b-form-invalid-feedback
                        class="feedback"
                        v-for="(errorKey, index) in validationContext.errors"
                        v-bind:key="index"
                      >
                        {{ $cte(errorKey) }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>

                  <ValidationObserver>
                    <b-form-row>
                      <b-form-group
                        :label="$cte('regExt.formLabelPass')"
                        class="col-12 col-md-6"
                      >
                        <ValidationProvider
                          :rules="{
                            required: true,
                            regex_pattern: { pattern: passwordRegex },
                          }"
                          vid="password"
                          mode="lazy"
                          v-slot="validationContext"
                        >
                          <b-form-input
                            v-model="loginData.password"
                            name="password"
                            autocomplete="new-password"
                            type="password"
                            id="password"
                            :state="getValidationState(validationContext)"
                            :placeholder="$cte('regExt.formPlaceholdPass')"
                            required
                          />

                          <b-form-invalid-feedback
                            class="feedback"
                            v-for="(
                              errorKey, index
                            ) in validationContext.errors"
                            v-bind:key="index"
                          >
                            {{ $cte(errorKey) }}
                          </b-form-invalid-feedback>
                        </ValidationProvider>
                      </b-form-group>

                      <b-form-group
                        :label="$cte('regExt.formLabelPassRepeat')"
                        class="col-12 col-md-6"
                      >
                        <ValidationProvider
                          :rules="{
                            required: true,
                            confirmed: 'password',
                            regex_pattern: { pattern: passwordRegex },
                          }"
                          mode="lazy"
                          v-slot="validationContext"
                        >
                          <b-form-input
                            v-model="loginData.passwordAgain"
                            type="password"
                            id="password_again"
                            :state="getValidationState(validationContext)"
                            :placeholder="
                              $cte('regExt.formPlaceholdPassRepeat')
                            "
                            required
                          />
                          <b-form-invalid-feedback
                            class="feedback"
                            v-for="(
                              errorKey, index
                            ) in validationContext.errors"
                            v-bind:key="index"
                          >
                            {{ $cte(errorKey) }}
                          </b-form-invalid-feedback>
                        </ValidationProvider>
                      </b-form-group>
                    </b-form-row>
                  </ValidationObserver>

                  <validation-provider
                    mode="lazy"
                    :rules="{ required: true }"
                    v-slot="validationContext"
                  >
                    <b-form-group :label="$cte('regExt.formLabelCountry')">
                      <b-form-select
                        v-model="loginData.country"
                        :options="countryOptions"
                        :state="getValidationState(validationContext)"
                      ></b-form-select>

                      <b-form-invalid-feedback
                        class="feedback"
                        v-for="(errorKey, index) in validationContext.errors"
                        v-bind:key="index"
                      >
                        {{ $cte(errorKey) }}
                      </b-form-invalid-feedback>
                    </b-form-group>
                  </validation-provider>

                  <p
                    class="small font-poppins"
                    v-html="
                      $t('regExt.labelAcceptedTermAndUse', {
                        privacyLink: `<u><a href='${privacyLink}' target='_blank'>${$cte(
                          'regExt.privacyLinkLabel'
                        )}</a></u>`,
                        termsLink: `<u><a href='${termsLink}' target='_blank'>${$cte(
                          'regExt.termsLinkLabel'
                        )}</a></u>`,
                      })
                    "
                  ></p>
                </div>
              </fieldset>

              <!-- Step 2 -->
              <fieldset v-if="currentStep === 2">
                <div class="step-2">
                  <div class="py-3 py-lg-5">
                    <h1 class="h3">{{ $cte("regExt.companyStepDesc") }}</h1>
                  </div>

                  <validation-provider mode="lazy">
                    <b-form-group>
                      <b-form-input v-model="hidden" hidden></b-form-input>
                    </b-form-group>
                  </validation-provider>

                  <validation-provider
                    v-if="step2ManualInput"
                    mode="lazy"
                    :rules="{ required: true }"
                    v-slot="validationContext"
                  >
                    <b-form-group :label="$cte('regExt.formLabelCompanyName')">
                      <b-form-input
                        v-model.trim="companyData.name"
                        autocomplete="off"
                        id="companyName"
                        type="text"
                        :state="getValidationState(validationContext)"
                        aria-describedby="companyName"
                        :placeholder="$cte('regExt.formLabelCompanyName')"
                      />
                    </b-form-group>
                  </validation-provider>

                  <validation-provider
                    v-if="!step2ManualInput"
                    mode="passive"
                    :rules="{ required: true }"
                    v-slot="validationContext"
                  >
                    <b-form-group :label="$cte('regExt.formLabelCompanyName')">
                      <LanogaSelect
                        v-model="companyData.name"
                        :options="selectOptions"
                        @select="selectCompanyName"
                        :required="true"
                      />
                      <div v-if="validationContext.errors[0]">
                        <div
                          class="feedback invalid-feedback d-block"
                          v-for="(errorKey, index) in validationContext.errors"
                          v-bind:key="index"
                        >
                          {{ $cte(errorKey) }}
                        </div>
                      </div>
                    </b-form-group>
                  </validation-provider>

                  <b-form-row>
                    <b-form-group
                      :label="$cte('regExt.taxNumber')"
                      :class="getTaxClass()"
                    >
                      <masked-input
                        v-model.trim="companyData.taxNumber"
                        autocomplete="off"
                        id="tax"
                        mask="########-#-##"
                        type="text"
                        :disabled="!step2ManualInput"
                        :state="maskedInputValid"
                        aria-describedby="tax"
                        :placeholder="$cte('regExt.taxNumber')"
                      />
                      <div
                        v-if="
                          taxNumberValid === true && maskedInputValid === false
                        "
                      >
                        <div class="feedback invalid-feedback d-block">
                          {{ $cte("validation.taxNumberLength") }}
                        </div>
                      </div>
                    </b-form-group>
                  </b-form-row>

                  <div class="my-4">
                    <label class="mr-2">{{
                      $cte("regExt.manualCompanyInputLabel")
                    }}</label>
                    <b-button variant="link" @click="onManualInputClick">{{
                      $cte("regExt.manualCompanyInput")
                    }}</b-button>
                  </div>
                </div>
              </fieldset>

              <!-- Step 3 -->
              <fieldset v-if="currentStep === 3">
                <div class="step-3">
                  <div class="py-3 py-lg-5">
                    <h1 class="h3">{{ $cte("regExt.personalDataTitle") }}</h1>
                  </div>

                  <b-form-row>
                    <b-form-group
                      :label="$cte('regExt.formLabelFirstName')"
                      class="col-12 col-md-6"
                    >
                      <validation-provider
                        mode="lazy"
                        :rules="{ required: true }"
                        v-slot="validationContext"
                      >
                        <b-form-input
                          v-model.trim="personalData.firstName"
                          :placeholder="$cte('regExt.formPlaceholdFirstName')"
                          :state="getValidationState(validationContext)"
                          type="text"
                          id="first-name"
                        />

                        <b-form-invalid-feedback
                          class="feedback"
                          v-for="(errorKey, index) in validationContext.errors"
                          v-bind:key="index"
                        >
                          {{ $cte(errorKey) }}
                        </b-form-invalid-feedback>
                      </validation-provider>
                    </b-form-group>

                    <b-form-group
                      :label="$cte('regExt.formLabelLastName')"
                      class="col-12 col-md-6"
                    >
                      <validation-provider
                        mode="lazy"
                        :rules="{ required: true }"
                        v-slot="validationContext"
                      >
                        <b-form-input
                          v-model.trim="personalData.lastName"
                          :placeholder="$cte('regExt.formPlaceholdLastName')"
                          :state="getValidationState(validationContext)"
                          type="text"
                          id="last-name"
                        />
                        <b-form-invalid-feedback
                          class="feedback"
                          v-for="(errorKey, index) in validationContext.errors"
                          v-bind:key="index"
                        >
                          {{ $cte(errorKey) }}
                        </b-form-invalid-feedback>
                      </validation-provider>
                    </b-form-group>
                  </b-form-row>

                  <b-form-row>
                    <b-form-group class="col-4" :label="$cte('regExt.formLabelBirthDate')">
                      <the-mask
                        v-model="personalData.birthDate"
                        class="form-control"
                        :mask="'####-##-##'"
                        type="text"
                        :masked="true"
                        placeholder=""
                      ></the-mask>
                      <div v-if="validateBirthDate() === false">
                        <div class="feedback invalid-feedback d-block">
                          {{ $cte("validation.birthDate") }}
                        </div>
                      </div>
                    </b-form-group>
                    <b-form-group
                        class="col-8"
                        :label="$cte('regExt.formLabelPhone')"
                    >
                      <vue-tel-input
                          v-model="personalData.phone"
                          :validCharactersOnly="true"
                          @blur="onBlurPhone"
                          @input="onInputPhone"
                          :placeholder="$cte('regExt.formPlaceholdPhone')"
                          :inputClasses="['form-control']"
                      ></vue-tel-input>

                      <div v-if="validatePhone() === false">
                        <div class="feedback invalid-feedback d-block">
                          {{ $cte("validation.phone") }}
                        </div>
                      </div>
                    </b-form-group>
                  </b-form-row>

                  <b-form-row>

                  </b-form-row>
                </div>
              </fieldset>

              <div class="nav-wrapper mt-5">

                <b-button
                  variant="primary"
                  class="float-right px-5"
                  type="submit"
                  ref="submit"
                  @click="setFieldsDirty(['phone', 'birthDate'])"
                  @submit="onSubmit(currentStep)"
                  :disabled="getStepDisabledState(currentStep + 1)"
                  >{{ $cte("regExt.btnNext") }}
                </b-button>
                <b-button
                    variant="secondary"
                    class="float-right mr-3"
                    v-if="currentStep > 1"
                    :disabled="getStepDisabledState(currentStep - 1)"
                    @click="changeStep(currentStep - 1)"
                >
                  {{ $cte("regExt.btnBack") }}
                </b-button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </ValidationObserver>
  </div>
</template>

<script>
import Api from "api-client";
import { TheMask } from "vue-the-mask";
import LanogaSelect from "@/components/LanogaSelect";
import StepIndicator from "./StepIndicator.vue";
import { mapActions } from "vuex";
import { VueTelInput } from "vue-tel-input";
import { ValidationProvider, ValidationObserver, extend } from "vee-validate";
import { required, email, confirmed } from "vee-validate/dist/rules";
import MaskedInput from "../../components/MaskedInput.vue";

extend("required", {
  ...required,
  message: "validation.required",
});

extend("email", {
  ...email,
  message: "validation.email",
});

extend("confirmed", {
  ...confirmed,
  message: "validation.emailSameAs",
});

extend("isMailUnique", {
  validate(value) {
    return Api.isEmailAvailablePublic(value)
      .then((res) => {
        return res.data;
      })
      .catch(() => {
        // even if the request is failed allow user to continue the registration
        return true;
      });
  },
  message: "validation.isMailUnique",
});

extend("regex_pattern", {
  validate(value, args) {
    return args.pattern.test(value);
  },
  params: ["pattern"],
  message: "validation.invalidPass",
});

export default {
  components: {
    LanogaSelect,
    StepIndicator,
    VueTelInput,
    ValidationProvider,
    ValidationObserver,
    MaskedInput,
    TheMask,
  },
  watch: {
    taxNumber(val) {
      if (val.length >= 1) {
        this.taxNumberValid = true;
      }
      if (val.length === 13) {
        this.maskedInputValid = true;
      } else {
        this.maskedInputValid = false;
      }
    },
    currentStep(val) {
      let stepNumber = "step-" + val;
      console.log(stepNumber);
      gtag( 'event', stepNumber);
    }
  },
  data() {
    return {
      step2ManualInput: false,
      maskedInputValid: false,
      taxNumberValid: false,
      validation: {
        phone: {
          dirty: false,
          valid: "",
        },
        birthDate: {
          dirty: false,
          valid: "",
        },
      },
      isStep2SubmitClicked: false,
      // LanogaSelect parameters
      selectOptions: {
        apiCallback: Api.getCompaniesAutoCompletePublic,
        formatText: function (val) {
          if (val && val.name) {
            return val.name;
          }
        },
        formatOption: function (val) {
          return val.name + " (" + val.full_tax_number + ")";
        },
        mapperCallback: function (data) {
          return data.result;
        },
        minimumInputLength: 3,
        searchOnServer: true,
        locale: {
          searchPlaceholder: "select.search",
          noResults: "select.noResults",
        },
      },
      helpLink: process.env.VUE_APP_HELP_LINK,
      loginLink: '/login',
      passwordRegex:
        /^(?=.*\d)(?=.*[a-z])(?!.*\s)(?=.*[A-Z])(?=.*[a-zA-Z]).{8,30}$/,
      privacyLink: 'https://acounto.com/adatkezelesi',
      termsLink: 'https://acounto.com/aszf',
      // Stepper state
      stepStates: [
        {
          id: 1,
          visible: true,
          disabled: false,
          validated: false,
          title: "regExt.loginStepTitle",
          description: "regExt.loginStepDesc",
        },
        {
          id: 2,
          visible: false,
          disabled: false,
          validated: false,
          title: "regExt.companyStepTitle",
          description: "regExt.companyStepDesc",
        },
        {
          id: 3,
          visible: false,
          disabled: false,
          validated: false,
          title: "regExt.personalStepTitle",
          description: "regExt.personalStepDesc",
        },
      ],
      // Select options,
      countryOptions: [
        {
          value: "Magyarország",
          text: "Magyarország",
        },
      ],
      // Form model data
      loginData: {
        country: "",
        email: "",
        password: "",
        passwordAgain: "",
      },
      companyData: {
        name: "",
        shortName: "",
        hasAccountant: false,
        taxNumber: null,
        companyType: null,
        roleInTheCompany: null,
        employeeNumber: "",
        flatRateType: null,
        costRatio: "",
        taxFormStartDate: null,
      },
      personalData: {
        firstName: "",
        lastName: "",
        birthDate: "",
        phone: "",
      },
      phoneValid: false,
      hidden: "",
    };
  },
  methods: {
    ...mapActions("app", ["login"]),

    init() {
      this.loginData.country = this.countryOptions[0].value;
    },

    afterSuccessFulLogin(auth, isAutoLogin) {
      let self = this;
      gtag( 'event', 'sign_up' ,);
      Api.getMyEnabledCompanies()
        .then((companyResponse) => {
          self.$store.dispatch("app/addCompanyList", companyResponse.data);
          document.title = "Acounto";
          self.$router.push("/app/home/" + new Date().valueOf());
        })
        .catch(() => {
          !isAutoLogin
            ? self.$toast.error({
                title: self.$cte("login.companiesQueryFailedTitle"),
                message: self.$cte("login.companiesQueryFailedMessage"),
              })
            : null;
        });
      self.$store.dispatch("login/login", auth);
      
      
    },
    setFieldsDirty(fieldsArray) {
      let self = this;
      if (self.currentStep === 3) {
        fieldsArray.forEach((field) => {
          self.validation[field].dirty = true;
        });
      }
    },

    getTaxClass() {
      return "col-12 col-md-12 mb-1";
    },

    validatePhone() {
      if (this.validation.phone.dirty) {
        if (this.personalData.phone) {
          this.validation.phone.valid = this.phoneValid;
          return this.phoneValid;
        } else {
          this.validation.phone.valid = false;
          return false;
        }
      }
    },
    onInputPhone: function (val, data) {
      this.phoneValid = data.isValid;
      this.validation.phone.dirty = true;
    },
    onManualInputClick() {
      this.step2ManualInput = !this.step2ManualInput;
      this.companyData = {
        name: "",
        shortName: "",
        taxNumber: null,
        hasAccountant: false,
        companyType: null,
        roleInTheCompany: null,
        employeeNumber: "",
        flatRateType: null,
        costRatio: "",
        taxFormStartDate: null,
      };
    },
    onBlurPhone: function () {
      this.validation.phone.dirty = true;
    },
    dateChanged(val) {
      this.personalData.birthDate = val;
      this.validation.birthDate.dirty = true;
      this.$emit("dateChange", val);
    },
    validateBirthDate() {
      if (this.validation.birthDate.dirty) {
        if (
          this.personalData.birthDate &&
          this.personalData.birthDate.length === 10
        ) {
          this.validation.birthDate.valid = true;
          return true;
        } else {
          this.validation.birthDate.valid = false;
          return false;
        }
      }
    },
    resetState() {
      Object.assign(this.$data, this.$options.data.call(this));
      this.$refs.observer.reset();
      this.init();
    },
    selectCompanyName(companySelectData) {
      this.companyData.taxNumber = companySelectData.full_tax_number;
      this.companyData.companyType = companySelectData.collection;
      this.companyData.shortName = companySelectData.short_name;
    },
    onSubmit() {
      if (this.currentStep === 3) {
        if (this.validation.birthDate.valid && this.validation.phone.valid) {
          this.saveUser();
        } else {
          return;
        }
      }
      if (this.currentStep === 2) {
        this.isStep2SubmitClicked = true;
        this.changeStep(this.currentStep + 1);
      } else {
        this.changeStep(this.currentStep + 1);
      }
    },
    login(self, auth) {
      self.$store.dispatch("login/login", auth);
      self.$router.push("app/home");
    },
    saveUser() {
      let self = this;

      if (!this.step2ManualInput) {
        let nameFromApi = this.companyData.name;
        this.companyData.name = nameFromApi.name;

        if (!nameFromApi.short_name) {
          this.companyData.shortName = this.companyData.name;
        }
      }

      let model = {
        ...this.loginData,
        ...this.companyData,
        ...this.personalData,
      };

      if (!model.shortName || model.shortName !== "") {
        model.shortName = model.name;
      }

      model.screenWidth = window.screen.width;
      model.screenHeight = window.screen.height;

  
      Api.signupExternal(model)
        .then((resp) => {
          self.$toast.success({
            title: self.$cte("toast.register-external-success"),
            message: self.$cte("toast.register-external-success"),
          });
          fbq('track', 'CompleteRegistration', {});
          this.afterSuccessFulLogin(resp.data, false)
        })
        .catch(() => {
          self.$toast.error({
            title: self.$cte("toast.register-external-failed"),
            message: self.$cte("toast.register-external-failed"),
          });
          self.resetState();
        });
    },
    getStepDisabledState(number) {
      return number < 1;
    },
    changeStep(number) {
      this.stepStates.forEach((elem) => {
        elem.visible = elem.id === number;
      });
      window.scrollTo(0, 0);
    },
    getValidationState({ dirty, validated, valid = null }) {
      return dirty || validated ? valid : null;
    },
  },
  computed: {
    currentStep() {
      let elem = this.stepStates.find((element) => element.visible);
      if (elem) {
        return elem.id;
      } else {
        return null;
      }
    },
    taxNumber: function () {
      return this.companyData.taxNumber;
    },
  },
  created() {
    window.sessionStorage.clear();
    this.init();
    document.title = this.$cte("register.registerDocTitle");
    gtag( 'event', "step-1");
  },
};
</script>

<style scoped>
.btn {
  border-radius: 7px;
  font-size: 0.8rem;
  padding: 7px 20px;
}

.btn-secondary {
  background-color: #edf5ec;
  color: #5fb661;
  border-color: transparent;
}

.btn-secondary.focus,
.btn-secondary:focus {
  box-shadow: 0 0 0 0.2rem rgba(229, 236, 228, 0.5);
}

.btn-secondary:not(:disabled):not(.disabled).active,
.btn-secondary:not(:disabled):not(.disabled):active {
  background-color: #e5ece4;
}

.btn-link {
  color: #5fb661;
  font-size: 1rem;
  font-weight: bold;
}

.btn-link:hover {
  text-decoration: underline;
  font-size: 1rem;
}

.custom-control-input:checked ~ .custom-control-label::before {
  color: #fff;
  border-color: #5fb661;
  background-color: #5fb661;
}

.form-control {
  border: 0;
  padding: 23px;
  padding-right: 35px;
  border-radius: 7px;
}

legend,
label {
  font-weight: 500;
  user-select: none;
}

.lanoga-select {
  font-size: 1rem;
  padding: 0 0.7rem;
  border-radius: 7px;
  border: none;
  height: 46px !important;
}

.select-option-list ul li:hover {
  background-color: #5fb661;
}

.form-control:disabled,
.form-control[readonly] {
  background-color: #d4d4d4;
}

.custom-select {
  height: 46px;
  border: none;
  border-radius: 7px;
  padding-left: 23px;
}

.form-group {
  margin-bottom: 1.5rem;
}

.container-fluid {
  font-family: "Poppins", sans-serif;
}

.steps-wrapper {
  //background: url("../../assets/img/register_external_bg.png") no-repeat center
  //  bottom;
  background: linear-gradient(180deg, #AFC9B2 0%, #AEBFC7 100%);
  box-shadow: 4px 0px 20px 0px rgba(0, 0, 0, 0.25);
}

.logo-wrapper {
  max-width: 240px;
}

a {
  color: #5fb661;
}

.bg-dark {
  background: #f3f5f9 !important;
}

.text-success {
  color: #5fb661;
}

.switch-helper-text {
  user-select: none;
}

.form-group :focus {
  outline: none;
  outline-color: unset;
  outline-style: none;
  outline-width: 0px;
}

.vue-tel-input {
  border: none;
}

.warning-message {
  color: #c45454;
  font-weight: bold;
}

.is-invalid > input:focus {
  border-color: red !important;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
  background-repeat: no-repeat;
  background-position: right calc(0.375em + 0.1875rem) center;
}

.vue-tel-input:focus-within {
  box-shadow: none;
}

.loginDataTitle {
  margin-top: -80px;
}
/*---MEDIA QUERIES---*/
@media only screen and (max-width: 650px) {
  #img {
    overflow: hidden;
    max-height: 120px;
    position: relative;
    flex: 1;
  }
  .loginDataTitle {
    margin-top: 0px;
  }
}
</style>

